/**
 * The dock card under the rail and the sidebar (§6.7): the voice row and you
 * (mic, sound, settings).
 */
import { VOICE_REGIONS, type SettableStatus } from '@neosocial/protocol';
import { tr, useT } from '../i18n';
import { api } from '../lib/api';
import { useData } from '../store/data';
import { presenceOf, privateChannelName } from '../store/selectors';
import { sendPresence } from '../store/session';
import { goHub, openSettings } from '../store/ui';
import { leaveVoice, toggleDeaf, toggleMute, toggleNoiseSuppression } from '../voice/controller';
import { myServerMute, myVoiceBlocked, signalQuality, type SignalQuality } from '../voice/machine';
import { channelStatusKey, micToggleView, serverStatusKey, soundToggleView } from '../voice/flags';
import { SelfToggleButton, useOwnServerFlags, useSelfLift } from '../voice/VoiceFlags';
import { useVoice, useVoiceQuiet } from '../voice/store';
import { Avatar, Icon, Ltr, Presence } from '../ui/kit';
import { openMenu, openPopover, toast } from '../ui/overlay';
import { updateData } from '../store/data';
import { openProfile } from '../hub/ProfileCard';
import { CameraButton, ShareButton, StreamingRow } from '../hub/Streams';
import { openInputMenu, openOutputMenu } from '../hub/voiceQuick';
import { copyText } from '../lib/clipboard';

/**
 * The room you are in, as the dock and the voice bar name it: its own name,
 * or "Private channel" for a room you cannot see (QA D2: a moderator moved
 * you into a hidden channel, which never reaches you; the dock read
 * "Voice connected" over "· 1 ms").
 */
function useSessionRoom(): { id: string | null; name: string } {
  const { t } = useT();
  const id = useVoice((v) => v.session.channelId ?? v.session.targetId);
  const name = useData((d) => {
    const c = id ? d.channels[id] : undefined;
    // a DM call (2026-11-01): the DM's name, as its header says it
    return c && !c.guild_id ? privateChannelName(d, c) : c?.name;
  });
  return { id, name: id ? (name ?? t('ns.voice.privateRoom')) : '' };
}

/** Opens the room you are in: a DM call's DM, or the voice room. */
function openSessionRoom(id: string | null): void {
  if (!id) return;
  const c = useData.getState().channels[id];
  goHub(c && !c.guild_id ? { kind: 'home', channelId: id } : { kind: 'voice', channelId: id });
}

/** The signal's quality in words, for the icon's tooltip and the connection popover. */
const QUALITY_KEY: Record<SignalQuality, string> = {
  good: 'vx.sig.good',
  ok: 'vx.sig.ok',
  bad: 'vx.sig.bad',
};

/**
 * The connection popover (Discord's on the voice panel's signal): the
 * quality, the ping and the packet loss since the connection began, live.
 */
function SignalDetails() {
  const { t, fmt } = useT();
  const rtt = useVoice((v) => v.session.rttMs);
  const loss = useVoice((v) => v.session.lossPct);
  const region = useVoice((v) => v.session.region);
  const regionTemp = useVoice((v) => v.session.regionTemporary);
  const regionName = VOICE_REGIONS.find((r) => r.id === region)?.name ?? region;
  const q = signalQuality(rtt, loss);
  return (
    <div className="nx-sigpop">
      <div className="nx-sigpop-h">
        <span className="nx-vsig" data-q={q ?? undefined} aria-hidden="true">
          <Icon name="signal" />
        </span>
        <span className="t">
          <b>{t('vx.sig.title')}</b>
          <small>{q ? t(QUALITY_KEY[q]) : t('vx.sig.measuring')}</small>
        </span>
      </div>
      <dl className="nx-sigpop-l">
        <div>
          <dt>{t('vx.sig.ping')}</dt>
          <dd>{rtt !== null ? <Ltr cls="nx-num">{fmt.ms(rtt)}</Ltr> : t('voice.measuring')}</dd>
        </div>
        <div>
          <dt>{t('vx.sig.loss')}</dt>
          <dd>
            {loss !== null ? (
              <Ltr cls="nx-num">
                {fmt.num(loss / 100, {
                  style: 'percent',
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1,
                })}
              </Ltr>
            ) : (
              t('voice.measuring')
            )}
          </dd>
        </div>
        {region ? (
          <div>
            <dt>{t('vx.sig.region')}</dt>
            <dd>{regionTemp ? t('vx.sig.regionTemp', { name: regionName }) : regionName}</dd>
          </div>
        ) : null}
      </dl>
    </div>
  );
}

/**
 * The dock's quick noise suppression (Discord's voice panel): on (the
 * last choice that was not Off) or off, mid-call; a switch, as the setting
 * it is. Its tooltip says which.
 */
function NoiseButton() {
  const { t } = useT();
  const mode = useVoice((v) => v.prefs.noiseSuppression);
  const on = mode !== 'off';
  return (
    <button
      type="button"
      role="switch"
      className={'nx-dbtn nx-dnoise' + (on ? '' : ' na')}
      aria-checked={on}
      aria-label={t('vx.ns.title')}
      data-tip={t('vx.nsq.tip', {
        mode: t(
          mode === 'strong' ? 'vx.ns.strong' : mode === 'standard' ? 'vx.ns.standard' : 'vx.ns.off',
        ),
      })}
      onClick={() => void toggleNoiseSuppression()}
    >
      <Icon name={on ? 'noise' : 'noise-off'} />
    </button>
  );
}

export function VoiceRow() {
  const { t, fmt } = useT();
  // draws no ring: a speaking edge does not render it again
  const s = useVoiceQuiet();
  const own = useOwnServerFlags();
  const lift = useSelfLift();
  const { id, name } = useSessionRoom();
  if (s.status === 'idle') return null;
  const connected = s.status === 'connected';
  const failed = s.status === 'failed';
  // a moderator's server mute / deafen, and channel mute / deafen here, said
  // in words (each cleared on its own undo, or the channel's on a move
  // away), and that you can undo it yourself when you can (`useSelfLift`)
  const modKey = connected ? serverStatusKey(own, lift) : null;
  const chKey = connected ? channelStatusKey(own, lift) : null;
  const label = failed
    ? t('ns.voice.lost')
    : connected
      ? t('voice.connected')
      : s.reconnecting
        ? t('ns.voice.reconnecting')
        : t('voice.connecting');
  // the signal coloured by the ping and the loss (good, ok, bad), Discord's
  const q = connected ? signalQuality(s.rttMs, s.lossPct) : null;
  const ping =
    s.rttMs !== null ? t('vx.sig.pingTip', { ms: fmt.ms(s.rttMs) }) : t('vx.sig.measuring');
  return (
    <>
      <div
        className="nx-vrow nx-arrive"
        data-state={failed ? 'failed' : connected ? 'connected' : 'connecting'}
      >
        {connected ? (
          // a click shows the connection's details (ping, packet loss)
          <button
            type="button"
            className="nx-vsig"
            data-q={q ?? undefined}
            aria-haspopup="dialog"
            aria-label={t('vx.sig.title')}
            data-tip={ping}
            onClick={(e) =>
              openPopover(e.currentTarget, () => <SignalDetails />, {
                placement: 'top-start',
                cls: 'nx-sigpop-wrap',
                label: t('vx.sig.title'),
              })
            }
          >
            <Icon name="signal" />
          </button>
        ) : (
          <span className="nx-vsig" aria-hidden="true">
            {failed ? <Icon name="alert" /> : <i className="nx-spin" />}
          </span>
        )}
        <span className="t">
          {/* "Voice connected · Room": back to the room (Discord's) */}
          <button
            type="button"
            className="nx-vrow-room"
            disabled={!id}
            onClick={() => openSessionRoom(id)}
          >
            <b className="nx-swap" data-k={s.status}>
              {label}
            </b>
            <small>
              <bdi>{name}</bdi>
              {connected ? (
                <>
                  {name ? ' · ' : null}
                  {s.rttMs !== null ? (
                    <Ltr cls="nx-num">{fmt.ms(s.rttMs)}</Ltr>
                  ) : (
                    <span className="nx-vmeas">{t('voice.measuring')}</span>
                  )}
                </>
              ) : failed && s.error ? (
                <>
                  {name ? ' · ' : null}
                  {t(`ns.voice.err.${s.error}`, { channel: name })}
                </>
              ) : null}
            </small>
          </button>
          {modKey ? (
            <span className="ns-vmod" role="status" data-mod-status="server">
              <Icon name="shield" />
              {t(modKey)}
            </span>
          ) : null}
          {chKey ? (
            <span className="ns-vmod" role="status" data-mod-status="channel">
              <Icon name="volume" />
              {t(chKey)}
            </span>
          ) : null}
        </span>
        {/* noise suppression, the camera and Go Live, as Discord's voice panel has them (§4.7) */}
        {connected ? (
          <>
            <NoiseButton />
            <CameraButton cls="nx-dbtn" channelId={s.channelId} />
            <ShareButton cls="nx-dbtn" channelId={s.channelId} />
          </>
        ) : null}
        <button
          type="button"
          className="nx-dbtn danger"
          aria-label={t('voice.leave')}
          data-tip={t('voice.leave')}
          onClick={() => leaveVoice()}
        >
          <Icon name={failed ? 'x' : 'phone-off'} />
        </button>
      </div>
      {/* streaming: Stop streaming in words under the row (Discord's panel) */}
      <StreamingRow />
    </>
  );
}

const STATUSES: SettableStatus[] = ['online', 'idle', 'dnd', 'invisible'];

async function setStatus(st: SettableStatus) {
  const prev = useData.getState().settings;
  updateData((d) => (d.settings ? { ...d, settings: { ...d.settings, status: st } } : d));
  sendPresence(st);
  try {
    await api.me.patchSettings({ status: st });
  } catch {
    if (prev) updateData((d) => ({ ...d, settings: prev }));
    sendPresence(prev?.status ?? 'online');
    toast(tr().t('ns.err.generic'), { tone: 'danger', icon: 'alert' });
  }
}

/**
 * Your microphone toggle (the dock's, and the phone's voice bar's): your own
 * mute stays yours (gray when off); a moderator's block turns it red, the
 * channel's is an amber badge (live bug 2026-10-28: it hid them, so a click
 * showed nothing). In a call with no microphone it says so, and opens Voice
 * & audio (HV-B08). A right-click or long-press opens the input menu
 * (Input device ›, the input mode), as Discord's.
 */
function MicButton() {
  const { t } = useT();
  const selfMute = useVoice((v) => v.session.selfMute);
  const selfDeaf = useVoice((v) => v.session.selfDeaf);
  // comms-voice's roster: a moderator's server mute or deafen (a deafen
  // silences you too, as Discord's does), a moderator's channel mute or
  // deafen where you are (2026-10-28), or no right to speak in this channel
  // (Speak denied). Say which; never look "live".
  const own = useOwnServerFlags();
  const lift = useSelfLift();
  const cantSpeak = useVoice((v) => myServerMute(v.session));
  const noMic = useVoice((v) => v.session.listenOnly && v.session.pid !== null);
  if (noMic)
    return (
      <button
        type="button"
        className="nx-dbtn off"
        aria-pressed={true}
        aria-label={t('ns.voice.noMicTip')}
        data-tip={t('ns.voice.noMicTip')}
        data-no-mic
        onClick={() => openSettings('voice')}
        onContextMenu={(e) => {
          e.preventDefault();
          openInputMenu(e.currentTarget);
        }}
      >
        <Icon name="mic-off" />
      </button>
    );
  const mic = micToggleView(
    {
      selfMute,
      selfDeaf,
      serverMute: own.serverMute,
      serverDeaf: own.serverDeaf,
      channelMute: own.channelMute,
      channelDeaf: own.channelDeaf,
      noTalk: own.noTalk || (cantSpeak && !own.serverMute),
      lift,
    },
    { name: 'dock.mute', undo: 'dock.unmute' },
  );
  return (
    <SelfToggleButton
      view={mic}
      cls="nx-dbtn"
      data={{
        'data-server-muted': own.serverMute || undefined,
        'data-channel-muted': own.channelMute || undefined,
      }}
      onClick={toggleMute}
      onMenu={openInputMenu}
    />
  );
}

export function MeRow() {
  const { t } = useT();
  const me = useData((d) => d.me);
  const status = useData((d) => d.settings?.status ?? 'online');
  const shown = useData((d) => (d.me ? presenceOf(d, d.me.id) : 'offline'));
  const custom = useData((d) => d.settings?.custom_status?.text ?? null);
  const selfDeaf = useVoice((v) => v.session.selfDeaf);
  const speaking = useVoice((v) => v.session.meSpeaking && !myVoiceBlocked(v.session));
  const own = useOwnServerFlags();
  const lift = useSelfLift();
  if (!me) return null;
  const name = me.global_name || me.username;
  const sound = soundToggleView(
    { selfDeaf, serverDeaf: own.serverDeaf, channelDeaf: own.channelDeaf, lift: lift.deaf },
    { name: 'dock.deafen', undo: 'dock.undeafen' },
  );
  const presLabel = t(`pres.${status}`);
  return (
    <div className="nx-me">
      <button
        type="button"
        className="nx-me-id"
        aria-haspopup="menu"
        aria-label={`${name}, ${presLabel}`}
        onClick={(e) => {
          const anchor = e.currentTarget;
          openMenu(
            anchor,
            [
              // the @handle is Latin: a left-to-right isolate keeps the @ in front beside a right-to-left name
              { header: `${name} · ⁦@${me.username}⁩` },
              ...STATUSES.map((k) => ({
                label: t(`pres.${k}`),
                sub:
                  k === 'dnd'
                    ? t('pres.dnd.sub')
                    : k === 'invisible'
                      ? t('pres.invisible.sub')
                      : undefined,
                iconNode: <Presence status={k} solo />,
                checked: status === k,
                run: () => void setStatus(k),
              })),
              { sep: true },
              {
                label: t('pres.profile'),
                icon: 'user' as const,
                run: () => openProfile(me.id, anchor),
              },
              {
                label: t('pres.copy'),
                icon: 'copy' as const,
                run: () =>
                  void copyText(me.username).then((ok) =>
                    ok
                      ? toast(t('c.copied'), { icon: 'check' })
                      : // refused: say so, with the name to copy by hand
                        toast(t('ns.copy.failed', { text: `@${me.username}` }), {
                          icon: 'alert',
                          tone: 'warn',
                          duration: 9000,
                        }),
                  ),
              },
            ],
            { placement: 'top-start', label: name, offset: 10 },
          );
        }}
      >
        <Avatar user={me} size={32} presence={shown} speaking={speaking} />
        <span className="t">
          <b dir="auto">{name}</b>
          <small className="st">
            <span className="a" dir="auto">
              {custom ?? presLabel}
            </span>
            <span className="b" dir="ltr">
              @{me.username}
            </span>
          </small>
        </span>
      </button>
      <MicButton />
      <SelfToggleButton
        view={sound}
        cls="nx-dbtn"
        data={{
          'data-server-deafened': own.serverDeaf || undefined,
          'data-channel-deafened': own.channelDeaf || undefined,
        }}
        onClick={toggleDeaf}
        // the output menu (Output device ›, Output volume), as Discord's
        onMenu={openOutputMenu}
      />
      <button
        type="button"
        className="nx-dbtn"
        aria-label={t('dock.settings')}
        data-tip={t('dock.settings')}
        aria-keyshortcuts="Control+,"
        onClick={() => openSettings('appearance')}
      >
        <Icon name="gear" />
      </button>
    </div>
  );
}

/**
 * The compact voice bar (§12): where the dock is out of sight (a phone's main
 * pane, a tablet with its drawer closed) while you are in voice and not in
 * the room itself. The room's name (a tap goes back to the room), your
 * microphone and Leave.
 */
export function VoiceBar() {
  const { t } = useT();
  // draws no ring: a speaking edge does not render it again
  const s = useVoiceQuiet();
  const room = useSessionRoom();
  if (s.status === 'idle') return null;
  const connected = s.status === 'connected';
  const failed = s.status === 'failed';
  const label = failed
    ? t('ns.voice.lost')
    : connected
      ? t('voice.connected')
      : s.reconnecting
        ? t('ns.voice.reconnecting')
        : t('voice.connecting');
  return (
    <section
      className="nx-vbar"
      data-state={failed ? 'failed' : connected ? 'connected' : 'connecting'}
      aria-label={t('ns.vbar.label')}
    >
      <button
        type="button"
        className="nx-vbar-room"
        aria-label={t('ns.vbar.open', { channel: room.name })}
        // a hidden room you are in opens too (VoiceRoom shows it by the roster)
        onClick={() => openSessionRoom(room.id)}
      >
        <span className="nx-vsig" aria-hidden="true">
          {connected ? (
            <Icon name="signal" />
          ) : failed ? (
            <Icon name="alert" />
          ) : (
            <i className="nx-spin" />
          )}
        </span>
        <span className="t">
          <b>{label}</b>
          <small>
            <bdi>{room.name}</bdi>
          </small>
        </span>
      </button>
      <MicButton />
      {/* Stop streaming where the dock is out of sight (Share itself stays off phones) */}
      {connected ? <ShareButton cls="nx-dbtn" channelId={s.channelId} /> : null}
      <button
        type="button"
        className="nx-dbtn danger"
        aria-label={t('voice.leave')}
        onClick={() => leaveVoice()}
      >
        <Icon name={failed ? 'x' : 'phone-off'} />
      </button>
    </section>
  );
}
